import { FC, useCallback, useEffect } from 'react'
import { PoiListing } from '../general/poi/PoiListing.tsx'
import { PoiCategorizedListing } from '../general/poi/PoiCategorizedListing.tsx'
import { PoiClickedEvent } from '../map/Geometry.tsx'
import { PoiFilter } from '@/js/editor/components/filter/PoiFilter.tsx'
import { AddPoi } from '@/js/editor/components/general/add/AddPoi.tsx'
import { useDisableActiveTool } from '@/js/editor/hooks/useControls.ts'
import { Poi } from '@/js/types'
import { useAtomValue, useSetAtom } from 'jotai'
import { poisAtom } from '@/js/editor/state/mapAtoms.ts'
import { filteredPoisAtom, isFilteringAtom } from '@/js/editor/state/filterAtoms.ts'
import { activePoiIdAtom, dirtyPoiIdsAtom, effectiveActivePoiAtom, selectPoiAtom } from '@/js/editor/state/poiAtoms.ts'
import { activeGeometryIndexAtom, setActiveGeometryAtom } from '@/js/editor/state/editorUiAtoms.ts'
import { useRegisteredLayer } from '@/js/editor/components/map/LayerRegistryContext.tsx'

type PoiListProps = {
    isCategorized?: boolean
}

export const PoiList: FC<PoiListProps> = ({ isCategorized }) => {
    const allPois = useAtomValue(poisAtom)
    const filteredPois = useAtomValue(filteredPoisAtom)
    const isFiltering = useAtomValue(isFilteringAtom)
    const activePoiId = useAtomValue(activePoiIdAtom)
    const activePoi = useAtomValue(effectiveActivePoiAtom)
    const dirtyPoiIds = useAtomValue(dirtyPoiIdsAtom)
    const selectPoi = useSetAtom(selectPoiAtom)
    const setActiveGeometry = useSetAtom(setActiveGeometryAtom)
    const activeGeometryIndex = useAtomValue(activeGeometryIndexAtom)
    const activeLayer = useRegisteredLayer(activePoiId, activeGeometryIndex)
    const disableActiveTool = useDisableActiveTool()

    const pois = isFiltering ? filteredPois : allPois

    const setPoi = useCallback(
        (poi: Poi) => {
            disableActiveTool()
            selectPoi(poi.id)
        },
        [disableActiveTool, selectPoi]
    )

    useEffect(() => {
        const editorElement = document.getElementById('mm-editor')
        const mapElement = document.getElementById('mm-map')
        const elements = [editorElement, mapElement].filter((element): element is HTMLElement => element !== null)

        if (elements.length === 0) {
            return
        }

        const poiClickHandler = (event: Event) => {
            const poiEvent = event as CustomEvent<PoiClickedEvent>
            const { poi, geometry } = poiEvent.detail

            if (String(activePoiId) !== String(poi.id)) {
                disableActiveTool()
                selectPoi(poi.id)
            }

            const geometryIndex = poi.geometries?.geometries.findIndex((candidate) => candidate === geometry) ?? -1

            if (geometryIndex >= 0) {
                setActiveGeometry({
                    geometry,
                    index: geometryIndex,
                })
            }
        }

        elements.forEach((element) => {
            element.addEventListener('bsn_poi_clicked', poiClickHandler)
        })

        return () => {
            elements.forEach((element) => {
                element.removeEventListener('bsn_poi_clicked', poiClickHandler)
            })
        }
    }, [activePoiId, activeLayer, disableActiveTool, selectPoi, setActiveGeometry])

    return (
        <div className={'flex flex-col flex-1 overflow-y-scroll'}>
            <div className={'flex px-5 pt-3 pb-5 items-center justify-between'}>
                <h1 className={'text-xl font-semibold'}>Karteneinträge</h1>
                <AddPoi onPoiCreated={setPoi} />
            </div>

            <PoiFilter />

            <div className={'flex-1 overflow-y-scroll'}>
                {isCategorized ? (
                    <PoiCategorizedListing
                        pois={pois}
                        activePoi={activePoi ?? undefined}
                        itemAction={setPoi}
                        flagged={{
                            condition: (poi) => dirtyPoiIds.has(String(poi.id)),
                            text: '*',
                        }}
                    />
                ) : (
                    <PoiListing
                        pois={pois}
                        activePoi={activePoi ?? undefined}
                        itemAction={setPoi}
                        flagged={{
                            condition: (poi) => dirtyPoiIds.has(String(poi.id)),
                            text: '*',
                        }}
                    />
                )}
            </div>
        </div>
    )
}
